import type { ChestCircumference } from './valueObjects/chestCircumference';
import type { ChildId } from './valueObjects/childId';
import type { HeadCircumference } from './valueObjects/headCircumference';
import type { Height } from './valueObjects/height';
import type { MeasureDate } from './valueObjects/measureDate';
import type { MeasurerId } from './valueObjects/measurerId';
import type { MeasurerType } from './valueObjects/measurerType';
import type { ParentId } from './valueObjects/parentId';
import type { Weight } from './valueObjects/weight';
import type { MeasureJsonPub, MeasureJsonPvt } from '@/types/domain/measure/entities/measureEntity';

import {
  EntityPub,
} from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 測定プロパティ非公開
 */
export type MeasurePropsPvt = {
  /** 測定日時 */
  measureDate: MeasureDate,
  /** 高さ */
  height?: Height,
  /** 体重 */
  weight?: Weight,
  /** 頭囲 */
  headCircumference?: HeadCircumference,
  /** 胸囲 */
  chestCircumference?: ChestCircumference,
  /** 測定ID */
  measurerId: MeasurerId,
  /** 測定タイプ */
  measurerType: MeasurerType,
  /** 親ID */
  parentId: ParentId,
  /** 子ID */
  childId: ChildId,
}

/**
 * 測定プロパティ公開
 */
export type MeasurePropsPub = Record<string, never>

/**
 * 測定Entity
 */
export class MeasureEntity extends EntityPub<
  MeasurePropsPvt,
  MeasureJsonPvt,
  MeasurePropsPub,
  MeasureJsonPub
> {
  /**
   * 削除タイプ
   */
  get deleteType(): DeleteType {
    return DeleteType.ParentChildId;
  }

  /**
   * 削除キー
   */
  get deleteKey(): string {
    return this.parentChildId;
  }

  toJsonData(): MeasureJsonPvt {
    return {
      measureDate: this.measureDate.toStringDate(),
      height: this.height ? this.height.value : undefined,
      weight: this.weight ? this.weight.value : undefined,
      headCircumference: this.headCircumference ? this.headCircumference.value : undefined,
      chestCircumference: this.chestCircumference ? this.chestCircumference.value : undefined,
      measurerId: this.measurerId.value,
      measurerType: this.measurerType.value,
      parentId: this.parentId.value,
      childId: this.childId.value,
    };
  }

  toJsonPubData(): MeasureJsonPub {
    return {};
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    return EntityName.WagakoNurseryMeasureMeasureEntity;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.measureDate.toStringDate()}#${this.parentId.value}_${this.childId.value}#${this.measurerType.value}#${this.measurerId.value}`;
  }

  /**
   * サブキー
   */
  get subKey(): string {
    return `${this.parentId.value}_${this.childId.value}#${this.measureDate.toStringDate()}#${this.measurerType.value}#${this.measurerId.value}`;
  }

  /**
   * 親子ID
   */
  get parentChildId(): string {
    return `${this.parentId.value}_${this.childId.value}`;
  }

  /**
   * 測定日時
   */
  get measureDate(): MeasureDate {
    return this._value.measureDate;
  }

  /**
   * 測定日時
   */
  set measureDate(measureDate: MeasureDate) {
    this._value.measureDate = measureDate;
  }

  /**
   * 身長
   */
  get height(): Height | undefined {
    return this._value.height;
  }

  /**
   * 身長
   */
  set height(height: Height | undefined) {
    this._value.height = height;
  }

  /**
   * 体重
   */
  get weight(): Weight | undefined {
    return this._value.weight;
  }

  /**
   * 体重
   */
  set weight(weight: Weight | undefined) {
    this._value.weight = weight;
  }

  /**
   * 頭囲
   */
  get headCircumference(): HeadCircumference | undefined {
    return this._value.headCircumference;
  }

  /**
   * 頭囲
   */
  set headCircumference(headCircumference: HeadCircumference | undefined) {
    this._value.headCircumference = headCircumference;
  }

  /**
   * 胸囲
   */
  get chestCircumference(): ChestCircumference | undefined {
    return this._value.chestCircumference;
  }

  /**
   * 胸囲
   */
  set chestCircumference(chestCircumference: ChestCircumference | undefined) {
    this._value.chestCircumference = chestCircumference;
  }

  /**
   * 測定者ID
   */
  get measurerId(): MeasurerId {
    return this._value.measurerId;
  }

  /**
   * 測定者ID
   */
  set measurerId(measurerId: MeasurerId) {
    this._value.measurerId = measurerId;
  }

  /**
   * 測定タイプ
   */
  get measurerType(): MeasurerType {
    return this._value.measurerType;
  }

  /**
   * 測定タイプ
   */
  set measurerType(measurerType: MeasurerType) {
    this._value.measurerType = measurerType;
  }

  /**
   * 親ID
   */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /**
   * 親ID
   */
  set parentId(parentId: ParentId) {
    this._value.parentId = parentId;
  }

  /**
   * 子ID
   */
  get childId(): ChildId {
    return this._value.childId;
  }

  /**
   * 子ID
   */
  set childId(childId: ChildId) {
    this._value.childId = childId;
  }
}
